import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import SchemaDetailContext, {
  PendingCreations,
} from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  ATTRIBUTE_CREATE_BULK,
  ATTRIBUTE_GROUP_UPDATE_BULK,
  ATTRIBUTE_UPDATE_BULK,
  ELEMENT_UPDATE,
  SCHEMA_UPDATE,
} from '@atom/graph/schema';
import { Button, Icon, Menu, Modal, Snackbar } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AttributeCreateBulkInput,
  AttributeGroupUpdateBulkInput,
  AttributeUpdateBulkInput,
  AttributeUpdateBulkInputItem,
  ElementUpdateInput,
  SchemaTree,
  SchemaUpdateInput,
} from '@atom/types/schema';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { getAttributeGroupsPayload } from '../schemaBuilder/subItemDetail/subItemDetailUtilities';

import { getModalValue, ModalVariant } from './SchemaStatusModalValues';

import classes from './schemaDetailHeader.module.css';

const { MenuItem } = Menu;

const HTTP_STATUS_CONFLICT = 422;

const styles = {
  publishButton: {
    backgroundColor: colors.neutral.white,
    color: colors.brand.purple,
  },
};

const SchemaStatus = () => {
  const {
    schemaTree,
    refetchSchemaTree,
    pendingUpdates,
    setPendingUpdates,
    pendingCreations,
    setPendingCreations,
    pendingGroupUpdates,
    setPendingGroupUpdates,
    pendingSchemaUpdates,
    setPendingSchemaUpdates,
  } = useContext(SchemaDetailContext);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [modalVariant, setModalVariant] = useState<ModalVariant>();

  const [updateSchema] = useMutation<
    { schemaUpdate: SchemaTree },
    { input: SchemaUpdateInput }
  >(SCHEMA_UPDATE);

  const [attributeUpdateBulk] = useMutation<
    { attributeUpdateBulk: SchemaTree },
    { input: AttributeUpdateBulkInput }
  >(ATTRIBUTE_UPDATE_BULK);

  const [attributeCreateBulk] = useMutation<
    { attributeCreateBulk: SchemaTree },
    { input: AttributeCreateBulkInput }
  >(ATTRIBUTE_CREATE_BULK);

  const [attributeGroupUpdateBulk] = useMutation<{
    attributeGroupUpdateBulk: SchemaTree;
    input: AttributeGroupUpdateBulkInput;
  }>(ATTRIBUTE_GROUP_UPDATE_BULK);

  const [updateElement] = useMutation<
    { elementUpdate: SchemaTree },
    { input: ElementUpdateInput }
  >(ELEMENT_UPDATE);

  const handleSchemaUpdate = async () => {
    setDisabled(true);
    const isPublished = modalVariant === ModalVariant.PUBLISH_CONFIRM;

    try {
      await updateSchema({
        variables: {
          input: {
            schemaId: schemaTree?.id,
            isPublished,
          },
        },
      });

      refetchSchemaTree();

      setModalVariant(null);
      setDisabled(false);
    } catch (err) {
      if (err?.networkError?.statusCode === HTTP_STATUS_CONFLICT) {
        setModalVariant(ModalVariant.DRAFT_ERROR);
      } else {
        Snackbar.error({ message: 'An unknown error occurred' });
      }
      setDisabled(false);
    }
  };

  const publishPendingChanges = async (input: AttributeUpdateBulkInput) => {
    await attributeUpdateBulk({
      variables: {
        input,
      },
    });
  };

  const publishedPendingGroupChanges = async (
    input: AttributeGroupUpdateBulkInput,
  ) => {
    await attributeGroupUpdateBulk({
      variables: {
        input,
      },
    });
  };

  const publishPendingCreations = async (input: AttributeCreateBulkInput) => {
    await attributeCreateBulk({
      variables: {
        input,
      },
    });
  };

  const publishPendingSchemaChanges = async (input: ElementUpdateInput) => {
    await updateElement({
      variables: {
        input,
      },
    });
  };

  const getPendingAttributeCreationPayload = (
    pendingCreationsData: PendingCreations,
    schemaId: any,
  ) => {
    setDisabled(true);
    const currentElement = pendingCreationsData[schemaId];

    const attributes = R.keys(currentElement).reduce((acc, groupKey) => {
      return [...acc, ...R.values(currentElement[groupKey])];
    }, []);

    return attributes.map(attribute =>
      R.omit(['id', 'isTempAttribute'], attribute),
    );
  };

  const handlePublishChanges = async () => {
    setDisabled(true);
    // Each element that has changed attributes must be updated individually
    // This creates an array of promises per updated element.
    const pendingUpdatePromises = R.keys(pendingUpdates).reduce(
      (acc, key) => [
        ...acc,
        publishPendingChanges({
          schemaId: key.toString(),
          attributes: R.values(pendingUpdates[key]).map(
            (attribute): AttributeUpdateBulkInputItem =>
              R.omit(['tempEnumsIndexes'], attribute),
          ),
        }),
      ],
      [],
    );

    // Each changed attribute groups must be updated individually
    // This creates an array of promises per updated group
    const pendingGroupUpdatePromises = R.keys(pendingGroupUpdates).reduce(
      (acc, key) => [
        ...acc,
        publishedPendingGroupChanges({
          schemaId: key.toString(),
          attributeGroups: R.values(pendingGroupUpdates[key]),
        }),
      ],
      [],
    );

    // Each element that has newly created attributes must be updated individually
    // This creates an array of promises per element with new attributes.
    const pendingCreationPromises = R.keys(pendingCreations).reduce(
      (acc, key) => [
        ...acc,
        publishPendingCreations({
          schemaId: key.toString(),
          // @ts-ignore
          attributes: getPendingAttributeCreationPayload(pendingCreations, key),
        }),
      ],
      [],
    );

    // Each element that has reordered attributes/groups must be updated individually
    // This creates an array of promises per element with the updates
    const pendingSchemaUpdatePromises = R.keys(pendingSchemaUpdates).reduce(
      (acc, key) => [
        ...acc,
        publishPendingSchemaChanges({
          schemaId: key.toString(),
          attributeGroups: getAttributeGroupsPayload(pendingSchemaUpdates[key]),
        }),
      ],
      [],
    );

    const cleanup = async () => {
      await refetchSchemaTree();

      setPendingGroupUpdates(null);
      setPendingUpdates(null);
      setPendingCreations(null);
      setPendingSchemaUpdates(null);
      setModalVariant(null);
      setDisabled(false);
    };

    try {
      await Promise.all([
        ...pendingUpdatePromises,
        ...pendingCreationPromises,
        ...pendingGroupUpdatePromises,
        ...pendingSchemaUpdatePromises,
      ]);
    } catch (error) {
      // close modal and reset state on error to prevent submit spam
      cleanup();
    }
    cleanup();
  };

  const modalValue = getModalValue(modalVariant);
  const hasPendingChanges =
    !isNilOrEmpty(pendingUpdates) ||
    !isNilOrEmpty(pendingCreations) ||
    !isNilOrEmpty(pendingGroupUpdates) ||
    !isNilOrEmpty(pendingSchemaUpdates);
  const onConfirm = hasPendingChanges
    ? () => handlePublishChanges()
    : () => handleSchemaUpdate();

  const getFooter = () => {
    return modalVariant === ModalVariant.DRAFT_ERROR ? (
      <Button
        color="primary"
        variant="contained"
        onClick={() => setModalVariant(null)}
      >
        {modalValue?.button}
      </Button>
    ) : (
      <div>
        <Button
          onClick={() => setModalVariant(null)}
          style={{ marginRight: '0.5rem' }}
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          color="primary"
          variant="contained"
          onClick={onConfirm}
        >
          {modalValue?.button}
        </Button>
      </div>
    );
  };

  const icon = schemaTree.isPublished ? 'lock' : 'edit';
  const text = schemaTree.isPublished ? 'published' : 'draft';
  const publishedOnClick = schemaTree.isPublished
    ? () => {}
    : () => setModalVariant(ModalVariant.PUBLISH_CONFIRM);
  const draftOnClick = !schemaTree.isPublished
    ? () => {}
    : () => setModalVariant(ModalVariant.DRAFT_CONFIRM);

  return (
    <>
      {hasPendingChanges ? (
        <Button
          style={styles.publishButton}
          variant="contained"
          onClick={() => setModalVariant(ModalVariant.PENDING_PUBLISH_CONFIRM)}
        >
          Publish
        </Button>
      ) : (
        <Menu
          icon={
            <div className={classes.statusContainer}>
              <Icon color={colors.neutral.white}>{icon}</Icon>
              <div className={classes.statusText}>{text}</div>
              <Icon color={colors.neutral.white}>arrow_drop_down</Icon>
            </div>
          }
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          noIconButton
        >
          <MenuItem
            key="published"
            onClick={publishedOnClick}
            selected={schemaTree.isPublished}
            startAdornment={<Icon>lock</Icon>}
          >
            <div className={classes.statusOption}>
              <div>PUBLISHED</div>
              {schemaTree.isPublished && (
                <Icon color={colors.brand.blue}>check</Icon>
              )}
            </div>
          </MenuItem>
          <MenuItem
            key="draft"
            onClick={draftOnClick}
            selected={!schemaTree.isPublished}
            startAdornment={<Icon>edit</Icon>}
          >
            <div className={classes.statusOption}>
              <div>DRAFT</div>
              {!schemaTree.isPublished && (
                <Icon color={colors.brand.blue}>check</Icon>
              )}
            </div>
          </MenuItem>
        </Menu>
      )}
      <Modal
        title={modalValue?.title}
        open={!isNilOrEmpty(modalVariant)}
        onCancel={() => setModalVariant(null)}
        footer={getFooter()}
      >
        {modalValue?.content}
      </Modal>
    </>
  );
};

export default SchemaStatus;
